import { useMemo, useState } from 'react';
import { Box, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { FiPlus } from 'react-icons/fi';
import { MdOutlineLocalPhone, MdChevronRight } from 'react-icons/md';
import SipIcon from '@/assets/icons/SipIcon';
import Button from 'components/Common/Button';
import Search from 'components/Common/Search';
import { useGetTelephonyEndpoints } from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { countryLabel, formatNumberOrExtension } from '@/utils/phoneNumberCapabilities';
import { titleCase } from 'utils/helperFunctions';
import { toCapabilityBadges } from '../capabilityBadges';
import CapabilityBadgeChip from './CapabilityBadgeChip';

type Props = {
  selectedId: number | null;
  onSelect: (row: TelephonyEndpoint) => void;
  onBuyNumber: () => void;
  onConnectSip: () => void;
};

export default function TelephonyNumbersList({ selectedId, onSelect, onBuyNumber, onConnectSip }: Props) {
  const theme = useTheme();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);

  const { data, isLoading } = useGetTelephonyEndpoints({ page: 1, per_page: 100 });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    const q = searchText?.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (row) =>
        row.phone_number?.toLowerCase().includes(q) ||
        row.friendly_name?.toLowerCase().includes(q) ||
        row.provider?.toLowerCase().includes(q)
    );
  }, [data, searchText]);
  const totalCount = data?.meta?.total_count ?? data?.data?.length ?? rows.length;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 360 },
        flexShrink: 0,
        bgcolor: '#fff',
        borderRadius: '6px',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box px={2.5} pt={2.5} pb={0}>
        <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={1.5}>
          <Box minWidth={0}>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 22,
                  lineHeight: 1.2,
                  color: '#111827',
                  letterSpacing: '-0.01em'
                }}
              >
                Numbers
              </Typography>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 28,
                  height: 22,
                  px: '8px',
                  borderRadius: '999px',
                  bgcolor: '#F3F4F6',
                  color: '#6B7280',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1
                }}
              >
                {totalCount}
              </Box>
            </Box>
            <Typography color='text.secondary' sx={{ fontSize: 13, mt: 0.75, lineHeight: 1.4 }}>
              Phone numbers and extensions assigned to your agents.
            </Typography>
          </Box>

          <Button
            variant='contained'
            color='primary'
            size='small'
            startIcon={<FiPlus size={16} strokeWidth={2.5} />}
            onClick={(e) => setAddAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 2,
              py: 0.85,
              fontWeight: 500,
              fontSize: 14,
              minHeight: 34,
              lineHeight: 1.2,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' }
            }}
          >
            Add
          </Button>

          <Menu
            anchorEl={addAnchor}
            open={Boolean(addAnchor)}
            onClose={() => setAddAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 300,
                p: 1,
                borderRadius: '6px',
                border: '1px solid #ECECEC',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setAddAnchor(null);
                onBuyNumber();
              }}
              sx={{
                py: 1.5,
                px: 1.5,
                mb: 0.75,
                alignItems: 'center',
                gap: 1.25,
                borderRadius: '6px',
                bgcolor: '#F8F9FA',
                '&:hover': { bgcolor: '#EBEBEF' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: theme.palette.primary.main }}>
                <MdOutlineLocalPhone size={22} />
              </ListItemIcon>
              <ListItemText
                primary='Buy Number'
                secondary='Choose a Twilio & Bandwidth Number'
                primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: '#111827' }}
                secondaryTypographyProps={{ fontSize: 12, color: '#6B7280' }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddAnchor(null);
                onConnectSip();
              }}
              sx={{
                py: 1.5,
                px: 1.5,
                alignItems: 'center',
                gap: 1.25,
                borderRadius: '6px',
                bgcolor: '#F8F9FA',
                '&:hover': { bgcolor: '#EBEBEF' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: theme.palette.primary.main }}>
                <SipIcon width={22} height={22} />
              </ListItemIcon>
              <ListItemText
                primary='Connect via SIP'
                secondary='SIP registration or SIP Trunking'
                primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: '#111827' }}
                secondaryTypographyProps={{ fontSize: 12, color: '#6B7280' }}
              />
            </MenuItem>
          </Menu>
        </Box>

        <Box my={2}>
          <Search
            fullWidth
            size='small'
            placeholder='Search number'
            debounceTime={300}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        </Box>
      </Box>

      <Box flex={1} overflow='auto' px={2.5} pb={2.5} minHeight={200}>
        {isLoading ? (
          <Box display='flex' justifyContent='center' py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography fontSize={13} color='#9CA3AF' textAlign='center' py={4}>
            {searchText?.trim() ? 'No numbers match your search.' : 'No phone numbers yet.'}
          </Typography>
        ) : (
          rows.map((row) => {
            const selected = row.id === selectedId;
            const badges = toCapabilityBadges(row.capabilities);
            const provider = row.provider ? titleCase(row.provider.replace(/_/g, ' ')) : '';
            const region = countryLabel(row.country_code);
            const meta = [provider, region].filter(Boolean).join(' · ');
            const listTitle = row.friendly_name?.trim() || formatNumberOrExtension(row);

            return (
              <Box
                key={row.id}
                onClick={() => onSelect(row)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '6px',
                  border: `1px solid ${selected ? theme.palette.primary.main : '#E5E7EB'}`,
                  bgcolor: '#fff',
                  px: 2,
                  py: 1.75,
                  mb: 1.25,
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: selected ? theme.palette.primary.main : theme.palette.primary.light }
                }}
              >
                <Box display='flex' alignItems='center' justifyContent='space-between' gap={1} mb={1}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: 15,
                      lineHeight: 1.3,
                      color: '#111827'
                    }}
                  >
                    {listTitle}
                  </Typography>
                  {selected && (
                    <Box sx={{ color: theme.palette.primary.main, display: 'inline-flex', flexShrink: 0 }}>
                      <MdChevronRight size={22} />
                    </Box>
                  )}
                </Box>

                {badges.length > 0 && (
                  <Box display='flex' flexWrap='wrap' gap='6px' mb={1.1}>
                    {badges.map((badge) => (
                      <CapabilityBadgeChip key={badge} badge={badge} />
                    ))}
                  </Box>
                )}

                {meta && (
                  <Typography color='text.secondary' sx={{ fontSize: 12, lineHeight: 1.3 }}>
                    {meta}
                  </Typography>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
